// src/utils/tactic/tacticStorage.js
// ─────────────────────────────────────────────────────────────────────────
// Local persistence for the tactic board: playbooks, the traced terrain and
// the map calibration all live in localStorage, and any of them can be moved
// between machines as a JSON file. No backend involved.
// ─────────────────────────────────────────────────────────────────────────

import { normalizePlaybook } from "./playbook";
import { DEFAULT_LANDMARKS, DEFAULT_TERRAIN, OBJECTIVE_ANCHOR_IDS } from "./mapTerrain";
import { DEFAULT_CORNERS } from "./homography";

const KEY_PLAYBOOKS = "fsrov_tactic_playbooks";
const KEY_TERRAIN = "fsrov_tactic_terrain";
const KEY_CORNERS = "fsrov_tactic_corners";
const KEY_LANDMARKS = "fsrov_tactic_landmarks";

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch (err) {
    console.warn(`[tactic] could not read ${key}:`, err);
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (err) {
    // Most likely the 5MB quota — worth surfacing, a silent failure here
    // looks exactly like a successful save.
    console.error(`[tactic] could not save ${key}:`, err);
    return false;
  }
}

// ── Playbooks ────────────────────────────────────────────────────────────

export function loadPlaybooks() {
  const list = readJson(KEY_PLAYBOOKS, []);
  if (!Array.isArray(list)) return [];
  return list.map(normalizePlaybook).filter(Boolean);
}

export function savePlaybook(playbook) {
  const list = loadPlaybooks();
  const stamped = { ...playbook, updatedAt: new Date().toISOString() };
  const at = list.findIndex((p) => p.id === playbook.id);
  if (at >= 0) list[at] = stamped;
  else list.unshift(stamped);
  const ok = writeJson(KEY_PLAYBOOKS, list);
  return ok ? stamped : null;
}

export function deletePlaybook(id) {
  const list = loadPlaybooks().filter((p) => p.id !== id);
  writeJson(KEY_PLAYBOOKS, list);
  return list;
}

// ── Terrain & calibration ────────────────────────────────────────────────

export function loadTerrain() {
  const t = readJson(KEY_TERRAIN, null);
  if (!t || !Array.isArray(t.walls) || !Array.isArray(t.brush)) return DEFAULT_TERRAIN;
  return t;
}

export function saveTerrain(terrain) {
  return writeJson(KEY_TERRAIN, terrain);
}

export function resetTerrain() {
  try {
    localStorage.removeItem(KEY_TERRAIN);
  } catch (err) {
    console.warn("[tactic] could not reset terrain:", err);
  }
  return DEFAULT_TERRAIN;
}

export function loadCorners() {
  const c = readJson(KEY_CORNERS, null);
  if (!c) return DEFAULT_CORNERS;
  const keys = ["blueBase", "topLeft", "redBase", "bottomRight"];
  const valid = keys.every(
    (k) => Array.isArray(c[k]) && Number.isFinite(c[k][0]) && Number.isFinite(c[k][1])
  );
  return valid ? c : DEFAULT_CORNERS;
}

export function saveCorners(corners) {
  return writeJson(KEY_CORNERS, corners);
}

export function resetCorners() {
  try {
    localStorage.removeItem(KEY_CORNERS);
  } catch (err) {
    console.warn("[tactic] could not reset corners:", err);
  }
  return DEFAULT_CORNERS;
}

// ── Landmarks ────────────────────────────────────────────────────────────
// Only the anchor half is stored; twins are derived, so a saved file can never
// describe a lopsided map.

const isPoint = (p) =>
  Array.isArray(p) && p.length === 2 && p.every((n) => Number.isFinite(n) && n >= 0 && n <= 1);

export function normalizeLandmarks(raw) {
  if (!raw || typeof raw !== "object") return DEFAULT_LANDMARKS;
  const out = { ...DEFAULT_LANDMARKS };
  for (const id of OBJECTIVE_ANCHOR_IDS) {
    if (isPoint(raw[id])) out[id] = [raw[id][0], raw[id][1]];
  }
  return out;
}

export function loadLandmarks() {
  return normalizeLandmarks(readJson(KEY_LANDMARKS, null));
}

export function saveLandmarks(landmarks) {
  return writeJson(KEY_LANDMARKS, landmarks);
}

export function resetLandmarks() {
  try {
    localStorage.removeItem(KEY_LANDMARKS);
  } catch (err) {
    console.warn("[tactic] could not reset landmarks:", err);
  }
  return DEFAULT_LANDMARKS;
}

// ── File import / export ─────────────────────────────────────────────────

const safeFileName = (name) =>
  String(name || "playbook")
    .trim()
    .replace(/[^\p{L}\p{N}\-_ ]/gu, "")
    .replace(/\s+/g, "-")
    .slice(0, 60) || "playbook";

function downloadJson(payload, filename) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick — Safari cancels the download if it goes too soon.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportPlaybookFile(playbook) {
  downloadJson(
    { kind: "fsrov-tactic-playbook", version: 2, playbook },
    `${safeFileName(playbook?.name)}.tactic.json`
  );
}

export function exportAllFile(playbooks, terrain, corners, landmarks) {
  downloadJson(
    { kind: "fsrov-tactic-bundle", version: 3, playbooks, terrain, corners, landmarks },
    `fs-tactic-bundle-${new Date().toISOString().slice(0, 10)}.json`
  );
}

/**
 * Read a .json export back in. Accepts a single playbook, a bundle, or a bare
 * playbook object, so a file hand-edited outside the app still loads.
 * @returns {{ playbooks: Array, terrain: Object|null, corners: Object|null }}
 */
export async function importFile(file) {
  const text = await file.text();
  const data = JSON.parse(text);

  if (data?.kind === "fsrov-tactic-bundle") {
    return {
      playbooks: (data.playbooks || []).map(normalizePlaybook).filter(Boolean),
      terrain: data.terrain || null,
      corners: data.corners || null,
      // Bundles written before landmarks were editable simply have none.
      landmarks: data.landmarks ? normalizeLandmarks(data.landmarks) : null,
    };
  }

  const single = normalizePlaybook(data?.playbook || data);
  if (!single) throw new Error("ไฟล์นี้ไม่ใช่ไฟล์แผน Tactic Board");
  return { playbooks: [single], terrain: null, corners: null, landmarks: null };
}

export function importPlaybooks(incoming = []) {
  const existing = loadPlaybooks();
  const byId = new Map(existing.map((p) => [p.id, p]));
  for (const p of incoming) byId.set(p.id, p);
  const merged = [...byId.values()].sort(
    (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
  );
  writeJson(KEY_PLAYBOOKS, merged);
  return merged;
}
